import {
  Container,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  ThemeProvider,
} from "@mui/material";
import React, { memo, useEffect, useMemo, useState } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { StyledButton } from "../styled/Button";
import { Search, Settings } from "@mui/icons-material";
import { StyledFormControl, menuStyles } from "../styled/StyledFormSelect";
import { School } from "../../types/school";
import dayjs, { Dayjs } from "dayjs";
import { ToolbarConfig } from "../../types/widget";
import { theme } from "../../consts/theme";
import { getTitle } from "../../utils/getSchoolName";
import { fetchSchoolsByCountry } from "../../services/api/schools.api";
import { StyledTextField } from "../styled/TextField";
import { debounce, set } from "lodash";

const WidgetToolbar = ({ config }: { config: ToolbarConfig }) => {
  const COUNTRIES = config.countryConfig?.countries || [];
  const schools = config.schools;
  const [school_options, setSchoolOptions] = useState(schools);
  const [country, setCountry] = useState(
    config.countryConfig !== undefined && config.countryConfig.required
      ? "Greece"
      : ""
  );
  const [selectedSchool, setSchool] = useState("");
  // Default to -15 days to match the initial API call for BoxPlot
  const defaultOffset = config.periodPicker ? -15 : -1;
  const offset = config.datepickerConfig?.maxInterval
    ? -config.datepickerConfig.maxInterval
    : defaultOffset;
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().add(offset, "day")
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [period, setPeriod] = useState<number>(15);
  const [localValue, setLocalValue] = useState(period);
  const [show, setShow] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // useEffect(() => {
  //   if (COUNTRIES.length === 1) {
  //     fetchSchoolsByCountry(COUNTRIES[0]).then((data) =>
  //       setSchoolOptions(data.data.data.map((school) => school.school_id))
  //     );
  //   }
  // });

  useEffect(() => {
    // Reset selectedSchool only if the current value is no longer in the options
    if (
      school_options &&
      !school_options.some((school) => school.school_id! === selectedSchool)
    ) {
      setSchool("");
    }
  }, [school_options]);

  useEffect(() => {
    // console.log(schools);
    setSchoolOptions(schools);
  }, [schools]);

  const handleSubmit = () => {
    const params = {
      country,
      school_id: selectedSchool,
      start_date: startDate?.format("DD/MM/YYYY").toString(),
      end_date: endDate?.format("DD/MM/YYYY").toString(),
      period: config.periodPicker ? period : undefined,
    };

    if (error === null) {
      // console.log("Error: ", error);
      config.queryCB(params);
      console.log("Submit");
      setShow(false);
    }
  };

  const handleClick = () => {
    setShow(true);
  };

  const handleCountryChange = (e: SelectChangeEvent) => {
    const country = e.target.value as string;
    setCountry(country);

    if (country === "") {
      setSchoolOptions(schools);
    } else {
      fetchSchoolsByCountry(country).then((data) => {
        setSchoolOptions(data.data.data);
      });
    }
  };

  const debouncedOnChange = useMemo(
    () =>
      debounce((val: number) => {
        if (val >= 1 && val <= 30) {
          setPeriod(val);
        } else {
          setLocalValue(1);
          setPeriod(1);
        }
      }, 500),
    []
  );

  const handlePeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    const num = parseInt(val, 10);

    setLocalValue(num);
    if (!isNaN(num)) {
      debouncedOnChange(num);
    }
  };

  return !show ? (
    <Container sx={styles.searchBar} maxWidth={false}>
      <StyledButton variant='contained' onClick={handleClick}>
        <Settings fontSize='small' />
      </StyledButton>
    </Container>
  ) : (
    <Container maxWidth={false}>
      <Grid container sx={styles.searchBar}>
        {config.datepicker && (
          <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label='Start Date'
                format='DD/MM/YYYY'
                defaultValue={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                disableFuture
                maxDate={endDate}
                onError={(newError) => setError(newError)}
                slotProps={{
                  textField: { size: "small", sx: { width: width } },
                }}
              />
            </LocalizationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label='End Date'
                format='DD/MM/YYYY'
                defaultValue={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                disableFuture
                minDate={startDate}
                maxDate={
                  config.datepickerConfig?.maxInterval !== undefined
                    ? startDate?.add(
                        config.datepickerConfig?.maxInterval,
                        "day"
                      )
                    : null
                }
                onError={(newError) => setError(newError)}
                slotProps={{
                  textField: { size: "small", sx: { width: width } },
                }}
              />
            </LocalizationProvider>
          </ThemeProvider>
        )}
        {config.periodPicker && (
          <StyledTextField
            label={"Interval"}
            type='number'
            value={localValue}
            onChange={handlePeriodChange}
            inputProps={{ min: 1, max: 30, step: 1 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>day(s)</InputAdornment>
              ),
              sx: {
                "& input": {
                  textAlign: "center",
                },
              },
            }}
            size='small'
            variant='outlined'
            sx={{ width: widthPicker }}
          />
        )}
        {config.country && COUNTRIES.length > 1 && (
          <StyledFormControl
            variant='outlined'
            size='small'
            sx={{ width: width }}
          >
            <InputLabel id='country'>Country</InputLabel>
            <Select
              labelId='country'
              value={country}
              onChange={(e) => handleCountryChange(e)}
              input={<OutlinedInput label='Country' />}
              renderValue={(selected: string) => selected}
              MenuProps={menuStyles}
            >
              {COUNTRIES.map((country, index) => {
                return (
                  <MenuItem key={index} value={country}>
                    <ListItemText primary={country} />
                  </MenuItem>
                );
              })}
              {!(
                config.countryConfig !== undefined &&
                config.countryConfig.required
              ) && (
                <MenuItem value={""}>
                  <ListItemText primary={"No Option"} />
                </MenuItem>
              )}
            </Select>
          </StyledFormControl>
        )}
        {school_options && (
          <StyledFormControl
            variant='outlined'
            size='small'
            sx={{ width: width }}
          >
            <InputLabel id='school'>School</InputLabel>
            <Select
              labelId='school'
              value={selectedSchool}
              onChange={(e) => setSchool(e.target.value as string)}
              input={<OutlinedInput label='Schools' />}
              MenuProps={menuStyles}
              renderValue={(selected: string) =>
                getTitle(school_options, selected)
              }
            >
              {school_options.map((school, index) => {
                return (
                  <MenuItem key={index} value={school.school_id}>
                    <ListItemText
                      primary={getTitle(school_options, school.school_id!)}
                    />
                  </MenuItem>
                );
              })}
              <MenuItem value={""}>
                <ListItemText primary={"No Option"} />
              </MenuItem>
            </Select>
          </StyledFormControl>
        )}
        <StyledButton variant='contained' onClick={handleSubmit}>
          <Search fontSize='small' />
        </StyledButton>
      </Grid>
    </Container>
  );
};

const width = {
  xs: "150px",
  sm: "180px",
  md: "200px",
  lg: "150px",
  xl: "200px",
};

const widthPicker = "120px";

const styles = {
  searchBar: {
    display: "flex",
    flexWrap: { sm: "wrap", md: "nowrap", lg: "wrap", xl: "nowrap" },
    alignItems: "center",
    justifyContent: {
      xs: "flex-end",
      sm: "flex-end",
      md: "flex-end",
      lg: "flex-end",
      xl: "flex-end",
    },
    gap: "10px",
    paddingBottom: "10px",
  },
};
export default WidgetToolbar;

